import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash } from "lucide-react";
import {
  deletePreset,
  getFoscamPresets,
  getFoscamSettings,
  getFoscamStreams,
  getPtzSpeed,
  postCodec,
  postFoscamHdr,
  postFoscamImage,
  postFoscamImageReset,
  postFoscamIr,
  postFoscamMirror,
  postFoscamOsd,
  postFoscamReboot,
  postFoscamStreamParam,
  postPtzSpeed,
} from "@client/api/foscam";
import { t } from "@client/lib/dictionary";
import { cn } from "@client/lib/cn";
import { foscamErrMsg } from "@client/lib/foscam/apiError";
import { useFoscamStore } from "@client/store/foscam";
import { useUserStore } from "@client/store/user";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@client/components/ui/alert-dialog";

/**
 * Nastavení → Kamery.
 *
 * Sem se přesunulo všechno, co dřív viselo v overlay tlačítku nad živým obrazem
 * (přepínač kodeku, mazání uložených pozic) plus rychlost otáčení, na kterou
 * existoval endpoint, ale žádné UI. Nad obrazem zůstalo jen to, co se bez obrazu
 * dělat nedá: mířit kamerou a uložit aktuální pozici.
 *
 * Karta je admin-only (jako Layout) — přepnutí kodeku, rychlost i mazání pozic
 * jsou na serveru za `requireAdmin`, takže neadminovi by se ukazovalo mrtvé UI.
 */

type PendingCodec = { slug: string; name: string; codec: "h264" | "h265" } | null;
type PendingDelete = { slug: string; preset: string } | null;
type PendingReboot = { slug: string; name: string } | null;
type PendingResolution = { slug: string; name: string; streamType: number; code: number; label: string } | null;

const SPEEDS = [0, 1, 2, 3, 4] as const;

/**
 * Mapa kód rozlišení → WxH je model-specific (ověřeno ffprobe). Klíč mapy je
 * substring modelu (productName z getDevInfo). Nabízíme jen kódy, které jsou
 * empiricky ověřené — kódy 1,3 se v configu vyskytují, ale nejsou potvrzené,
 * tak je v UI jako volbu nenabízíme.
 */
const RESOLUTION_MAP: Record<string, Record<number, string>> = {
  MDS2031: { 0: "1280×720", 7: "1920×1080" },
  MGS4010: { 0: "1280×720", 7: "1920×1080", 9: "2304×1536" },
  MCS4020: { 0: "1280×720", 7: "1920×1080", 9: "2560×1440" },
};

/** Najde mapu rozlišení pro daný model (klíč = substring modelu). */
const resMapFor = (model: string): Record<number, string> | null => {
  const key = Object.keys(RESOLUTION_MAP).find((k) => model.includes(k));
  return key ? RESOLUTION_MAP[key] : null;
};

/** Posuvníky obrazu — pořadí = pořadí zobrazení. `denoise` je read-only (chybí zde). */
const IMAGE_SLIDERS = ["brightness", "contrast", "hue", "saturation", "sharpness"] as const;
type ImageSliderKey = (typeof IMAGE_SLIDERS)[number];

const uptime = (ms: number): string => {
  if (ms <= 0) return "—";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

const FoscamTabPanel: React.FC = () => {
  const foscams = useUserStore((s) => s.foscams);
  const caps = useFoscamStore((s) => s.caps);
  const presets = useFoscamStore((s) => s.presets);
  const setPresets = useFoscamStore((s) => s.setPresets);
  const fetchCaps = useFoscamStore((s) => s.fetchCaps);

  const [streams, setStreams] = useState<FoscamStreamRuntime[]>([]);
  const [speeds, setSpeeds] = useState<Record<string, number | null>>({});
  const [settings, setSettings] = useState<Record<string, FoscamSettings>>({});
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [pendingCodec, setPendingCodec] = useState<PendingCodec>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
  const [pendingReboot, setPendingReboot] = useState<PendingReboot>(null);
  const [pendingResolution, setPendingResolution] = useState<PendingResolution>(null);

  // Debounce posuvníků obrazu — jeden timeout na kombinaci kamera+veličina,
  // ať tažení posuvníkem neposílá desítky requestů.
  const imageDebounce = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Úklid naplánovaných debounce timerů při odmontování karty — jinak by pozdní POST
  // doletěl na kameru i po opuštění Nastavení (stray zápis + toast).
  useEffect(() => {
    const timers = imageDebounce.current;
    return () => {
      for (const id of Object.values(timers)) clearTimeout(id);
    };
  }, []);

  const capsList = Object.values(caps);
  // Stabilní podpisy online kamer. Efekty se musí přespustit i když se změní jen
  // `online` flag (počet kamer se nemění — caps drží i offline kamery). Bez toho by
  // kamera, co se připojí až po otevření karty (např. po deploy restartu), nikdy
  // nenačetla nastavení/PTZ/presety až do reloadu stránky.
  const onlineSig = capsList.filter((c) => c.online).map((c) => c.slug).sort().join(",");
  const ptzOnlineSig = capsList.filter((c) => c.hasPtz && c.online).map((c) => c.slug).sort().join(",");
  const presetOnlineSig = capsList.filter((c) => c.hasPresets && c.online).map((c) => c.slug).sort().join(",");

  // Diagnostika streamů + aktuální rychlosti se načtou při otevření karty.
  // Když endpoint není k dispozici (mimo RPi), zůstane to prostě prázdné.
  useEffect(() => {
    let alive = true;
    getFoscamStreams()
      .then((r) => alive && setStreams(r.responseObject ?? []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    for (const c of capsList) {
      if (!c.hasPtz || !c.online) continue;
      getPtzSpeed(c.slug)
        .then((r) => alive && setSpeeds((prev) => ({ ...prev, [c.slug]: r.responseObject?.speed ?? null })))
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
    // Přespustit při změně množiny online PTZ kamer (ne jen počtu).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptzOnlineSig]);

  // Plné nastavení kamery (model, stream, obraz, OSD, zrcadlení, IR, HDR).
  // Načte se jednou při otevření karty pro každou online kameru; null sekce
  // z backendu = kamera funkci nepodporuje a příslušná sekce se skryje.
  useEffect(() => {
    let alive = true;
    for (const c of capsList) {
      if (!c.online) continue;
      getFoscamSettings(c.slug)
        .then((r) => {
          if (alive && r.responseObject) setSettings((prev) => ({ ...prev, [c.slug]: r.responseObject as FoscamSettings }));
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
    // Přespustit při změně množiny online kamer (ne jen počtu) — jinak kamera, co
    // přejde offline→online, nikdy nenačte nastavení.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineSig]);

  const loadPresets = useCallback(
    (slug: string) => {
      getFoscamPresets(slug)
        .then((r) => setPresets(slug, r.responseObject?.presets ?? []))
        .catch(() => setPresets(slug, []));
    },
    [setPresets]
  );

  useEffect(() => {
    for (const c of capsList) {
      if (c.hasPresets && c.online && presets[c.slug] === undefined) loadPresets(c.slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetOnlineSig, loadPresets]);

  const applyCodec = async () => {
    if (!pendingCodec) return;
    const { slug, codec } = pendingCodec;
    setBusySlug(slug);
    setPendingCodec(null);
    try {
      const res = await postCodec(slug, codec);
      toast.success(res.message);
      // Server broadcastuje `foscam:caps` po přeprobnutí, ale refresh je jistota
      // pro případ, že by WS zrovna nebyla připojená.
      await fetchCaps();
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applyReboot = async () => {
    if (!pendingReboot) return;
    const { slug } = pendingReboot;
    setBusySlug(slug);
    setPendingReboot(null);
    try {
      const res = await postFoscamReboot(slug);
      toast.success(res.message || t.settings.foscam.rebootStarted);
      // Caps se NEobnovují: kamera je teď stejně pryč a `probeCapabilities` by jen
      // spolehlivě spadl do timeoutu. Server ji sám najde, až se vrátí na síť.
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applySpeed = async (slug: string, speed: number) => {
    setBusySlug(slug);
    try {
      await postPtzSpeed(slug, speed);
      setSpeeds((prev) => ({ ...prev, [slug]: speed }));
      toast.success(t.settings.foscam.speedSaved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applyDelete = async () => {
    if (!pendingDelete) return;
    const { slug, preset } = pendingDelete;
    setBusySlug(slug);
    setPendingDelete(null);
    try {
      await deletePreset(slug, preset);
      toast.success(t.foscam.deleted);
      loadPresets(slug);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  // Obraz: optimisticky updatni lokální stav hned (plynulý posuvník) a zápis
  // na kameru pošli až po odmlce. denoise sem nechodí (read-only).
  const applyImage = (slug: string, key: ImageSliderKey, val: number) => {
    setSettings((prev) => {
      const s = prev[slug];
      if (!s || !s.image) return prev;
      return { ...prev, [slug]: { ...s, image: { ...s.image, [key]: val } } };
    });
    const dk = `${slug}:${key}`;
    clearTimeout(imageDebounce.current[dk]);
    imageDebounce.current[dk] = setTimeout(() => {
      postFoscamImage(slug, { [key]: val } as Partial<FoscamImageSettings>)
        .then(() => toast.success(t.settings.foscam.saved))
        .catch((err) => toast.error(foscamErrMsg(err)));
    }, 400);
  };

  const applyImageReset = async (slug: string) => {
    // Zruš naběhlé debounce zápisy posuvníků, jinak by opožděný POST se starou hodnotou
    // přepsal právě resetnutý obraz (kamera by skončila v předresetovém stavu).
    for (const id of Object.values(imageDebounce.current)) clearTimeout(id);
    imageDebounce.current = {};
    setBusySlug(slug);
    try {
      await postFoscamImageReset(slug);
      const r = await getFoscamSettings(slug);
      if (r.responseObject) setSettings((prev) => ({ ...prev, [slug]: r.responseObject as FoscamSettings }));
      toast.success(t.settings.foscam.saved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applyOsd = async (slug: string, key: "timeStamp" | "devName" | "tempHumid", cur: boolean) => {
    setBusySlug(slug);
    try {
      await postFoscamOsd(slug, { [key]: !cur } as { timeStamp?: boolean; devName?: boolean; tempHumid?: boolean });
      setSettings((prev) => {
        const s = prev[slug];
        if (!s || !s.osd) return prev;
        // Cast: computed-key spread by jinak z dispPos:number udělal konfliktní
        // index signaturu (ostatní klíče jsou boolean).
        const nextOsd = { ...s.osd, [key]: !cur } as FoscamSettings["osd"];
        return { ...prev, [slug]: { ...s, osd: nextOsd } };
      });
      toast.success(t.settings.foscam.saved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applyMirror = async (slug: string, key: "isMirror" | "isFlip", cur: boolean) => {
    setBusySlug(slug);
    try {
      await postFoscamMirror(slug, { [key]: !cur } as { isMirror?: boolean; isFlip?: boolean });
      setSettings((prev) => {
        const s = prev[slug];
        if (!s || !s.mirror) return prev;
        return { ...prev, [slug]: { ...s, mirror: { ...s.mirror, [key]: !cur } } };
      });
      toast.success(t.settings.foscam.saved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  // IR: Auto = mode 0; Zapnuto/Vypnuto = mode 1 + ruční ledOn. V settings držíme
  // jen `mode` (stav LED kamera nevrací), takže po zápisu ukládáme jen mode.
  const applyIr = async (slug: string, mode: number, ledOn?: boolean) => {
    setBusySlug(slug);
    try {
      await postFoscamIr(slug, ledOn === undefined ? { mode } : { mode, ledOn });
      setSettings((prev) => {
        const s = prev[slug];
        if (!s || !s.ir) return prev;
        return { ...prev, [slug]: { ...s, ir: { ...s.ir, mode } } };
      });
      toast.success(t.settings.foscam.saved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applyHdr = async (slug: string, on: boolean) => {
    setBusySlug(slug);
    try {
      await postFoscamHdr(slug, on ? 1 : 0);
      setSettings((prev) => {
        const s = prev[slug];
        if (!s || !s.hdr) return prev;
        return { ...prev, [slug]: { ...s, hdr: { mode: on ? 1 : 0 } } };
      });
      toast.success(t.settings.foscam.saved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  const applyResolution = async () => {
    if (!pendingResolution) return;
    const { slug, streamType, code } = pendingResolution;
    setBusySlug(slug);
    setPendingResolution(null);
    try {
      await postFoscamStreamParam(slug, { sub: false, streamType, resolution: code });
      setSettings((prev) => {
        const s = prev[slug];
        if (!s) return prev;
        const main = s.stream.main.slice();
        const cur = main[streamType];
        if (cur) main[streamType] = { ...cur, resolution: code };
        return { ...prev, [slug]: { ...s, stream: { ...s.stream, main } } };
      });
      toast.success(t.settings.foscam.saved);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusySlug(null);
    }
  };

  if (foscams.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">{t.settings.foscam.none}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t.settings.foscam.description}</p>

      {capsList.length === 0 && (
        <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
          {t.settings.foscam.onlyRpi}
        </p>
      )}

      {foscams.map((f) => {
        const cap = capsList.find((c) => c.name === f.name);
        if (!cap) return null;
        const stream = streams.find((s) => s.slug === cap.slug);
        const busy = busySlug === cap.slug;
        const camPresets = presets[cap.slug] ?? [];
        const st = settings[cap.slug] as FoscamSettings | undefined;
        // Předpočítané ne-null sekce — narrowing v ternách drží ve scope mapy
        // (uvnitř vnořených IIFE by se ztratilo). null = kamera funkci nemá.
        const img = st?.image ?? null;
        const osd = st?.osd ?? null;
        const mirror = st?.mirror ?? null;
        const ir = st?.ir ?? null;
        const hdr = st?.hdr ?? null;

        return (
          <section key={f.name} className="rounded-lg border border-border p-3">
            <header className="mb-3 flex flex-wrap items-center gap-2">
              <span
                className={cn("h-2.5 w-2.5 shrink-0 rounded-full", cap.online ? "bg-status-ok" : "bg-muted-foreground")}
                aria-hidden="true"
              />
              <h3 className="text-sm font-semibold">{f.name}</h3>
              {cap.hasPtz && <Badge variant="secondary">{t.settings.foscam.capsPtz}</Badge>}
              {cap.hasZoom && <Badge variant="secondary">{t.settings.foscam.capsZoom}</Badge>}
              {cap.hasPresets && <Badge variant="secondary">{t.settings.foscam.capsPresets}</Badge>}
            </header>

            {!cap.online && <p className="mb-3 text-xs text-status-warning">{t.settings.foscam.offline}</p>}

            {/* Kodek */}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
              <div>
                <div className="text-sm font-medium">{t.foscam.codec}</div>
                <div className="text-xs text-muted-foreground">
                  {t.settings.foscam.codecCurrent}: {cap.codec.toUpperCase()} · {t.foscam.codecNote}
                </div>
              </div>
              {cap.canSwitchCodec ? (
                <div className="flex gap-1">
                  {(["h264", "h265"] as const).map((c) => (
                    <Button
                      key={c}
                      type="button"
                      size="sm"
                      variant={cap.codec === c ? "default" : "outline"}
                      disabled={busy || cap.codec === c}
                      onClick={() => setPendingCodec({ slug: cap.slug, name: f.name, codec: c })}
                    >
                      {c === "h264" ? "H.264" : "H.265"}
                    </Button>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">{t.settings.foscam.codecUnavailable}</span>
              )}
            </div>

            {/* Rychlost otáčení */}
            {cap.hasPtz && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                <div>
                  <div className="text-sm font-medium">{t.settings.foscam.speedLabel}</div>
                  <div className="text-xs text-muted-foreground">{t.settings.foscam.speedHint}</div>
                </div>
                <div className="flex gap-1">
                  {SPEEDS.map((s) => (
                    <Button
                      key={s}
                      type="button"
                      size="sm"
                      variant={speeds[cap.slug] === s ? "default" : "outline"}
                      disabled={busy || !cap.online}
                      onClick={() => void applySpeed(cap.slug, s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Uložené pozice — mazání. Ukládání zůstává u kamery (vyžaduje živý obraz). */}
            {cap.hasPresets && (
              <div className="mb-3 border-b border-border pb-3">
                <div className="text-sm font-medium">{t.foscam.presets}</div>
                <div className="mb-2 text-xs text-muted-foreground">{t.settings.foscam.presetsHint}</div>
                {camPresets.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t.settings.foscam.presetsNone}</p>
                ) : (
                  <ul className="flex flex-wrap gap-1">
                    {camPresets.map((p) => (
                      <li key={p} className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs">
                        {p}
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                          aria-label={`${t.foscam.presetDelete} ${p}`}
                          disabled={busy}
                          onClick={() => setPendingDelete({ slug: cap.slug, preset: p })}
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Kvalita hlavního streamu */}
            {cap.online &&
              st &&
              st.stream.mainType !== null &&
              (() => {
                const mt = st.stream.mainType as number;
                const prof = st.stream.main[mt];
                const resMap = resMapFor(st.model);
                if (!prof || !resMap) return null;
                const codes = Object.keys(resMap).map(Number);
                return (
                  <div className="mb-3 border-b border-border pb-3">
                    <div className="text-sm font-medium">{t.settings.foscam.streamQuality}</div>
                    <div className="mb-2 text-xs text-muted-foreground">
                      {t.settings.foscam.resolutionCurrent}:{" "}
                      <span className="font-medium">{resMap[prof.resolution] ?? `#${prof.resolution}`}</span> ·{" "}
                      {t.settings.foscam.streamQualityHint}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {codes.map((code) => (
                        <Button
                          key={code}
                          type="button"
                          size="sm"
                          variant={prof.resolution === code ? "default" : "outline"}
                          disabled={busy || prof.resolution === code}
                          onClick={() =>
                            setPendingResolution({
                              slug: cap.slug,
                              name: f.name,
                              streamType: mt,
                              code,
                              label: resMap[code] ?? `#${code}`,
                            })
                          }
                        >
                          {resMap[code]}
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              })()}

            {/* Obraz */}
            {cap.online && img && (
              <div className="mb-3 border-b border-border pb-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">{t.settings.foscam.image}</div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void applyImageReset(cap.slug)}
                  >
                    {t.settings.foscam.resetImage}
                  </Button>
                </div>
                <div className="flex flex-col gap-2">
                  {IMAGE_SLIDERS.map((key) => (
                    <label key={key} className="flex items-center gap-2 text-xs">
                      <span className="w-24 shrink-0 text-muted-foreground">{t.settings.foscam[key]}</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={img[key]}
                        disabled={!cap.online}
                        className="h-1.5 flex-1 cursor-pointer accent-primary"
                        onChange={(e) => applyImage(cap.slug, key, Number(e.target.value))}
                      />
                      <span className="w-8 shrink-0 text-right tabular-nums">{img[key]}</span>
                    </label>
                  ))}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-24 shrink-0">{t.settings.foscam.denoise}</span>
                    <span className="flex-1">{t.settings.foscam.denoiseReadonly}</span>
                    <span className="w-8 shrink-0 text-right tabular-nums">{img.denoise}</span>
                  </div>
                </div>
              </div>
            )}

            {/* OSD */}
            {cap.online && osd && (
              <div className="mb-3 border-b border-border pb-3">
                <div className="text-sm font-medium">{t.settings.foscam.osd}</div>
                <div className="mb-2 text-xs text-muted-foreground">{t.settings.foscam.osdHint}</div>
                <div className="flex flex-wrap gap-1">
                  {(
                    [
                      { key: "timeStamp", label: t.settings.foscam.timestamp },
                      { key: "devName", label: t.settings.foscam.devName },
                      { key: "tempHumid", label: t.settings.foscam.tempHumid },
                    ] as const
                  ).map((it) => (
                    <Button
                      key={it.key}
                      type="button"
                      size="sm"
                      variant={osd[it.key] ? "default" : "outline"}
                      disabled={busy}
                      onClick={() => void applyOsd(cap.slug, it.key, osd[it.key])}
                    >
                      {it.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Zrcadlení / překlopení */}
            {cap.online && mirror && (
              <div className="mb-3 border-b border-border pb-3">
                <div className="text-sm font-medium">{t.settings.foscam.mirror}</div>
                <div className="mb-2 text-xs text-muted-foreground">{t.settings.foscam.mirrorHint}</div>
                <div className="flex flex-wrap gap-1">
                  {(
                    [
                      { key: "isMirror", label: t.settings.foscam.mirrorH },
                      { key: "isFlip", label: t.settings.foscam.flipV },
                    ] as const
                  ).map((it) => (
                    <Button
                      key={it.key}
                      type="button"
                      size="sm"
                      variant={mirror[it.key] ? "default" : "outline"}
                      disabled={busy}
                      onClick={() => void applyMirror(cap.slug, it.key, mirror[it.key])}
                    >
                      {it.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Noční přisvícení (IR) */}
            {cap.online && ir && (
              <div className="mb-3 border-b border-border pb-3">
                <div className="text-sm font-medium">{t.settings.foscam.ir}</div>
                <div className="mb-2 text-xs text-muted-foreground">{t.settings.foscam.irHint}</div>
                <div className="flex flex-wrap gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={ir.mode === 0 ? "default" : "outline"}
                    disabled={busy}
                    onClick={() => void applyIr(cap.slug, 0)}
                  >
                    {t.settings.foscam.irAuto}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void applyIr(cap.slug, 1, true)}
                  >
                    {t.settings.foscam.irOn}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void applyIr(cap.slug, 1, false)}
                  >
                    {t.settings.foscam.irOff}
                  </Button>
                </div>
              </div>
            )}

            {/* HDR — jen modely, které ho podporují (backend vrací null jinak). */}
            {cap.online && hdr && (
              <div className="mb-3 border-b border-border pb-3">
                <div className="text-sm font-medium">{t.settings.foscam.hdr}</div>
                <div className="mb-2 text-xs text-muted-foreground">{t.settings.foscam.hdrHint}</div>
                <div className="flex flex-wrap gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant={hdr.mode === 1 ? "default" : "outline"}
                    disabled={busy || hdr.mode === 1}
                    onClick={() => void applyHdr(cap.slug, true)}
                  >
                    {t.settings.foscam.hdrOn}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={hdr.mode === 0 ? "default" : "outline"}
                    disabled={busy || hdr.mode === 0}
                    onClick={() => void applyHdr(cap.slug, false)}
                  >
                    {t.settings.foscam.hdrOff}
                  </Button>
                </div>
              </div>
            )}

            {/* Restart kamery — poslední záchrana, když se kamera zasekne nebo visí na slabém AP. */}
            <div className="mb-3 border-b border-border pb-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={busy || !cap.online}
                onClick={() => setPendingReboot({ slug: cap.slug, name: f.name })}
              >
                {t.settings.foscam.reboot}
              </Button>
            </div>

            {/* Diagnostika streamu */}
            {stream && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>
                  {t.settings.foscam.streamState}: <span className="font-medium">{stream.state}</span>
                </span>
                {stream.ip && (
                  <span>
                    {t.settings.foscam.streamIp}: {stream.ip}
                  </span>
                )}
                <span>
                  {t.settings.foscam.streamBitrate}: {stream.bitrateKbps} kb/s
                </span>
                <span>
                  {t.settings.foscam.streamUptime}: {uptime(stream.uptimeMs)}
                </span>
                <span>
                  {t.settings.foscam.streamLastData}: {Math.round(stream.lastDataAgoMs / 1000)} s
                </span>
                <span>
                  {t.settings.foscam.streamFailures}: {stream.failures}
                </span>
              </div>
            )}
          </section>
        );
      })}

      {/* Potvrzení přepnutí kodeku — restartuje stream, což uživatel uvidí. */}
      <AlertDialog open={pendingCodec !== null} onOpenChange={(o) => !o && setPendingCodec(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.settings.foscam.codecConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCodec?.name} → {pendingCodec?.codec === "h264" ? "H.264" : "H.265"}.{" "}
              {t.settings.foscam.codecConfirmBody}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void applyCodec()}>{t.app.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Potvrzení smazání pozice */}
      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.foscam.presetDelete}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.foscam.presetDeleteConfirm} „{pendingDelete?.preset}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.cancel}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:opacity-90" onClick={() => void applyDelete()}>
              {t.foscam.presetDelete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Potvrzení restartu kamery. Dialog je ZÁMĚRNĚ mimo `foscams.map()` — uvnitř by
          vznikl jeden pro každou kameru a v DOM by jich viselo tolik, kolik je kamer. */}
      <AlertDialog open={pendingReboot !== null} onOpenChange={(o) => !o && setPendingReboot(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.settings.foscam.rebootConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReboot?.name}. {t.settings.foscam.rebootConfirmBody}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.cancel}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:opacity-90" onClick={() => void applyReboot()}>
              {t.settings.foscam.reboot}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Potvrzení změny rozlišení — přenastavení profilu krátce restartuje stream. */}
      <AlertDialog open={pendingResolution !== null} onOpenChange={(o) => !o && setPendingResolution(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.settings.foscam.resolutionConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingResolution?.name} → {pendingResolution?.label}. {t.settings.foscam.resolutionConfirmBody}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.app.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void applyResolution()}>{t.app.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default React.memo(FoscamTabPanel);
